import React, { useMemo } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Box, ButtonBase, Stack } from '@mui/material';
import {
  useCurrentBlockId,
  useHoverState,
  useMutateCurrentHover,
  useSetCurrentBlockId,
} from '@wp-block-builder/editor/hooks';

import BlockViewLabel from './BlockViewLabel';
import { TFlatBlock } from './helpers/blocksToFlatDocument';
import { useBlockAncestorCollapsed, useBlockCollapsed, useToggleCollapseBlockId } from './NavigatorStore';

type TOverBlockStatus = {
  id: string;
  position: 'before' | 'after';
};

type Props = {
  flatBlock: TFlatBlock;
  activeBlock: TFlatBlock | null | undefined;
  setOverBlockStatus: (status: null | TOverBlockStatus) => void;
};

export default function BlockView({ activeBlock, flatBlock, setOverBlockStatus }: Props) {
  const isHover = useHoverState(flatBlock.block) === 'self';
  const hoverMutator = useMutateCurrentHover();
  const { allowChildren, block, id } = flatBlock;
  const currentBlockId = useCurrentBlockId();
  const setCurrentBlockId = useSetCurrentBlockId();
  const toggleBlock = useToggleCollapseBlockId();
  const isAncestorCollapsed = useBlockAncestorCollapsed(flatBlock);
  const isSelfCollapsed = useBlockCollapsed(flatBlock);

  const onChevronClick = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleBlock(flatBlock.id);
  };

  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (ev) => {
    if (activeBlock) {
      const rect = ev.currentTarget.getBoundingClientRect();
      const lineY = rect.y + rect.height / 2;
      const position = ev.clientY < lineY ? 'before' : 'after';
      setOverBlockStatus({ id, position });
    }
  };

  const onMouseEnter = () => hoverMutator.push(block);
  const onMouseLeave = () => hoverMutator.remove(block.id);
  const onClick = (ev: React.MouseEvent) => {
    ev.preventDefault();
    setCurrentBlockId(flatBlock.id);
  };

  const isDragging = useMemo(() => {
    if (!activeBlock) {
      return false;
    }
    if (flatBlock.parentIds.includes(activeBlock.id)) {
      return true;
    }
    return id === activeBlock.id;
  }, [activeBlock, flatBlock, id]);
  const isSelected = useMemo(() => currentBlockId === flatBlock.id, [currentBlockId, flatBlock]);
  const isParentSelected = useMemo(() => {
    if (currentBlockId === null) {
      return false;
    }
    return flatBlock.parentIds.includes(currentBlockId);
  }, [currentBlockId, flatBlock]);

  const { attributes, listeners, setNodeRef: setDraggableNodeRef } = useDraggable({ id });

  const renderChevronIcon = () => {
    const width = 16;
    if (!allowChildren) {
      return <Box sx={{ width, height: width }} />;
    }

    const IconComponent = isSelfCollapsed ? ChevronRightOutlined : ExpandMoreOutlined;
    return (
      <ButtonBase onClick={onChevronClick} sx={{ color: isSelected ? 'white' : 'text.secondary' }}>
        <IconComponent sx={{ my: -0.5, width, fontSize: 14, color: 'inherit' }} />
      </ButtonBase>
    );
  };

  const currentBackgroundColor = () => {
    if (isSelected) {
      return 'brand.blue';
    }
    if (isHover) {
      return 'grey.100';
    }
    return undefined;
  };

  if (isAncestorCollapsed) {
    return null;
  }

  if (isParentSelected && activeBlock) {
    return null;
  }

  return (
    <Box
      sx={{
        cursor: isDragging ? 'grabbing' : 'pointer',
        backgroundColor: currentBackgroundColor(),
      }}
      onMouseMove={onMouseMove}
    >
      <Stack
        ref={setDraggableNodeRef}
        {...attributes}
        {...listeners}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        alignItems="stretch"
        direction="row"
        px={1}
      >
        {renderTreeMarkers(flatBlock.depth)}
        <Stack alignItems="center" direction="row" sx={{ py: 0.5, width: '100%' }}>
          {renderChevronIcon()}
          <BlockViewLabel selected={isSelected} flatBlock={flatBlock} />
        </Stack>
      </Stack>
    </Box>
  );
}

function renderTreeMarkers(depth: number) {
  const result: JsxNode[] = [];
  for (let count = 0; count < depth; count++) {
    result.push(<Box key={count} marginX={['8px', '7px']} borderLeft="1px solid" borderColor="divider" />);
  }
  return result;
}
